import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface TrackPricing {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  price: number;
  description: string;
  duration_hours: number;
  use_traction_compound: boolean;
  is_active: boolean;
  deposit_percentage: number;
  deposit_amount: number;
  blocks_full_day: boolean;
}

export interface TrackInfo {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  description: string;
  features: string[];
  is_active: boolean;
}

export interface AddOnService {
  id: string;
  name: string;
  description: string;
  price: number;
  is_active: boolean;
}

export interface UserProfile {
  role: string;
}

export interface DateValidation {
  date: string;
  isValid: boolean;
  error?: string;
}

export const useTrackData = (selectedTrackType: 'drag' | 'drift', userProfile: UserProfile | null) => {
  const [pricing, setPricing] = useState<TrackPricing[]>([]);
  const [trackInfo, setTrackInfo] = useState<TrackInfo | null>(null);
  const [addOns, setAddOns] = useState<AddOnService[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightingFee, setLightingFee] = useState(0);

  const fetchPricing = async () => {
    try {
      const { data, error } = await supabase
        .from('track_pricing')
        .select('*')
        .eq('track_type', selectedTrackType)
        .eq('is_active', true)
        .order('duration_hours', { ascending: true });

      if (error) throw error;
      
      // Filter out full day options for driver users
      let filteredData = data || [];
      if (userProfile?.role === 'driver') {
        filteredData = filteredData.filter(item => !item.blocks_full_day);
      }
      
      setPricing(filteredData);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('track_info')
        .select('*')
        .eq('track_type', selectedTrackType)
        .single();

      if (error) throw error;
      setTrackInfo(data);
    } catch (error) {
      console.error('Error fetching track info:', error);
    }
  };

  const fetchAddOns = async () => {
    try {
      const { data, error } = await supabase
        .from('add_on_services')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setAddOns(data || []);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
    }
  };

  const fetchLightingFee = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'lighting_fee_amount')
        .single();

      if (error) throw error;
      setLightingFee(parseFloat(data?.setting_value || '0'));
    } catch (error) {
      console.error('Error fetching lighting fee:', error);
    }
  };

  useEffect(() => {
    fetchPricing();
    fetchTrackInfo();
    fetchAddOns();
    fetchLightingFee();
  }, [selectedTrackType, userProfile?.role]);

  return {
    pricing,
    trackInfo,
    addOns,
    loading,
    lightingFee,
    setLightingFee
  };
};